import React from 'react';
import transition from '../../transition';
import CaseStudy from '../CaseStudy/CaseStudy';
import '../Project.css';

import hero from '../../assets/images/projects/menu-maker/menu-maker-hero.png';
import MenuMakerImage1 from '../../assets/images/projects/menu-maker/menu-maker-login.png';
import MenuMakerImage2 from '../../assets/images/projects/menu-maker/menu-maker-dashboard.png';
import MenuMakerImage3 from '../../assets/images/projects/menu-maker/menu-maker-editor-large.png';
import MenuMakerImage4 from '../../assets/images/projects/menu-maker/menu-maker-display.jpg';
import MenuMakerImage5 from '../../assets/images/projects/menu-maker/menu-maker-christmas.png';
import MenuMakerImage6 from '../../assets/images/projects/menu-maker/menu-maker-options.png';
import MenuMakerImage7 from '../../assets/images/projects/menu-maker/menu-maker-preview.png';

const MenuMaker = () => {
  return (
    <CaseStudy
      className="menuMaker"
      accent="var(--menuMaker)"
      eyebrow="Product · Digital signage"
      title="Menu Maker"
      summary="A free, phone-first menu board system for small restaurants that can’t justify enterprise signage prices."
      links={[
        {
          href: 'https://kevinfuller.me/projects/menu-maker/login.php',
          label: 'Open demo'
        },
        {
          href: '#frames',
          label: 'See it in action'
        }
      ]}
      hero={{
        src: hero,
        alt: 'Menu Maker editor over BuffaLouie digital menu boards',
        caption: 'BuffaLouie · live boards + editor'
      }}
      date="January 2022"
      role="Software engineer & lead — product, UI, and backend"
      stack={['PHP', 'MySQL', 'JavaScript', 'HTML/CSS']}
      brief={{
        label: 'Background',
        they: {
          title: 'The kitchen',
          body: 'BuffaLouie’s runs digital menu boards on TVs around the dining room. Prices, sold-out items, and specials change during service. Guests only see what’s on the wall, so the boards have to stay honest in real time.'
        },
        me: {
          title: 'My role',
          body: 'I was their software engineer and the lead on Menu Maker. I designed and built the product end to end, then opened it up free to other Bloomington businesses when word got around.'
        }
      }}
      problem={{
        title: 'Small restaurants were stuck between duct tape and a second rent payment.',
        lead: 'There wasn’t good public software for this at a price a small kitchen could afford.',
        detail:
          'BuffaLouie’s was stuck on static Illustrator boards pushed to Intel sticks. Fine until something sold out mid-dinner. The “real” digital signage products locked the same basic job behind hard monthly fees, often in the same ballpark as hiring another staff member. That pricing preys on smaller restaurants that only need fast updates, not an enterprise package.'
      }}
      metrics={[
        { value: 'Free', label: 'For Bloomington businesses' },
        { value: '5+', label: 'Local spots on live boards' },
        { value: '2 wks', label: 'First usable version' },
        { value: 'Phone-first', label: 'Updates between tables' }
      ]}
      outcome="Word spread around Bloomington. Other businesses asked in, and I made Menu Maker free for them."
      solutionsLabel="What shipped"
      solutions={[
        {
          title: 'See it run',
          lead: 'From login to a live board update in one pass.',
          detail:
            'This walkthrough is the product: edit items on a phone, preview the board, push the change. No design file. No USB stick. No monthly bill that eats into payroll.',
          video: 'https://www.youtube.com/embed/FPQaCRg4JP8?si=pddoB_0ET5AbWGqq',
          videoCaption: 'Full walkthrough of Menu Maker in use.'
        },
        {
          title: 'The editor',
          lead: 'Per-restaurant logins, item stock toggles, pricing, and board accents.',
          detail:
            'Built for a one-handed phone session between tables. Managers get their own accounts, each board can look different, and a sold-out item can come down before the next ticket prints.',
          images: [
            {
              src: MenuMakerImage3,
              alt: 'Menu item editor',
              caption: 'Item editor: stock, pricing, section headers.'
            },
            {
              src: MenuMakerImage7,
              alt: 'Mobile preview',
              caption: 'Preview the board before it hits the TV.'
            },
            {
              src: MenuMakerImage6,
              alt: 'Customization options',
              caption: 'Accent color, titles, and media without Illustrator.'
            }
          ]
        },
        {
          title: 'Accounts & boards',
          lead: 'Separate logins and a dashboard for switching between boards.',
          detail:
            'No shared password on a sticky note in the kitchen. Each restaurant gets its own account, and staff can jump between boards without leaving the phone.',
          images: [
            {
              src: MenuMakerImage1,
              alt: 'Login screen',
              caption: 'Per-restaurant accounts.'
            },
            {
              src: MenuMakerImage2,
              alt: 'Dashboard',
              caption: 'Dashboard for switching between boards.'
            }
          ]
        },
        {
          title: 'On the wall, around town',
          lead: 'It started at BuffaLouie’s. Then other Bloomington kitchens wanted in.',
          detail:
            'Once people saw live boards updating mid-rush, word traveled. I kept Menu Maker free for local businesses instead of turning it into another gated monthly subscription.',
          images: [
            {
              src: MenuMakerImage4,
              alt: 'Digital boards on display at BuffaLouie',
              caption: 'Live boards on the wall.'
            },
            {
              src: MenuMakerImage5,
              alt: 'Festive mode',
              caption: 'Festive mode for the holiday stretch.'
            }
          ]
        }
      ]}
      decisions={[
        {
          title: 'Build instead of rent',
          body: 'Public signage tools either looked dated or charged small restaurants like enterprises. Building it meant owning the workflow and keeping the price at zero for local kitchens.'
        },
        {
          title: 'Phone before desktop',
          body: 'Most updates happen mid-shift. The editor and preview are built for a one-handed phone session, not a design suite.'
        },
        {
          title: 'Keep the board honest',
          body: 'In/out of stock, festive mode, and live preview close the gap between what the kitchen knows and what guests see on the wall.'
        },
        {
          title: 'Free for Bloomington',
          body: 'When other local businesses asked after word got around, I gave them accounts at no cost. The point was usable boards, not another subscription eating into payroll.'
        }
      ]}
      next={{ link: '/projects/crimsonflags', label: 'Crimson Flags' }}
    />
  );
};

export default transition(MenuMaker);
